// src/components/friendship/FriendCirclesPromo.jsx
//
// Friend Circles on the homepage. Every line here has to be true of the app
// as built (docs/CIRCLES_PULSE.md). That ruled out what this section used to
// say: a mock feed of reflections and reactions, a presence dot, "4/5
// members", "3-5 members per circle", an anonymous-sharing option, a "2.4x
// more likely to complete" figure nobody measured, and a quote from a "beta
// tester, age 16". Circles no longer have any of the features those described.
import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Users } from 'lucide-react';
import { getPulseQuestion } from '../../data/circlePulseQuestions';

// An illustration of a revealed question, with the modules' own characters.
// Read inside the component, never at module scope. The production build puts
// this page and the data it reads in different chunks that import each other
// (the "Circular chunk" warnings), and a module-scope read ran before the data
// chunk had initialised: "Cannot access '…' before initialization", and the
// whole app rendered a blank page on load.
const EXAMPLE_PICKS = [['Leo'], ['Zara', 'Danny'], ['Priya'], []];

const FriendCirclesPromo = ({ onJoinClick }) => {
  const EXAMPLE = getPulseQuestion('pulse-recharge');
  const features = [
    {
      title: 'The same module',
      description: 'Your circle picks one of the eight modules. You can see whether your friends have finished it. Not how far, not how fast, and nothing anyone wrote.',
      emoji: '📖',
    },
    {
      title: 'One question, everyone at once',
      description: 'Everyone picks an answer from a few fixed options. Nobody sees anyone else’s until everyone has, then they all show together, with names. The interesting bit is where you differ.',
      emoji: '🙋',
    },
    {
      title: 'Then talk, in person',
      description: 'A few questions to take with you for the walk home. There’s nowhere in a circle to type an answer, on purpose.',
      emoji: '🚶',
    },
  ];

  const facts = [
    'No posts, comments, reactions or chat',
    'Answers are about yourself, never about anyone in the circle',
    'Nobody runs a circle, and nobody can remove anyone',
    'Anyone can leave at any time, and nobody is told',
    'Send the code privately; get a new one if it gets out',
    'For people aged 13 and over',
  ];

  return (
    <section className="relative py-20 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 overflow-hidden">
      <div className="absolute inset-0 opacity-10" aria-hidden="true">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-400/30 rounded-full px-4 py-2 mb-6">
            <Sparkles aria-hidden="true" className="w-4 h-4 text-purple-300" />
            <span className="text-purple-200 font-medium text-sm">Friend Circles</span>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Do the modules
            <span className="block mt-2 bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              with your friends
            </span>
          </h2>

          <p className="text-xl text-purple-200 max-w-3xl mx-auto leading-relaxed">
            Start a circle with friends you already know. Read the same module, answer the same question, and
            talk about it when you&apos;re together.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-start mb-14">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-gradient-to-br from-purple-800/40 to-blue-800/40 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border border-purple-500/30 shadow-2xl"
          >
            <p className="text-xs uppercase tracking-wide text-purple-300 mb-3">Example: a question, revealed</p>
            <h3 className="text-xl font-bold text-white mb-4">{EXAMPLE.question}</h3>
            <ul className="space-y-2">
              {EXAMPLE.options.map((option, i) => (
                <li key={option} className="bg-white/5 rounded-lg px-4 py-3">
                  <p className="text-white text-sm">{option}</p>
                  {EXAMPLE_PICKS[i].length > 0 && (
                    <p className="text-xs text-amber-200 mt-1">{EXAMPLE_PICKS[i].join(', ')}</p>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-sm text-purple-200 mt-4">
              Hidden until everyone had answered, then shown to all of them at once. Now they ask each other why.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-5"
          >
            <h3 className="text-2xl font-bold text-white">How it works</h3>
            {features.map((feature) => (
              <div
                key={feature.title}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-5 sm:p-6 border border-purple-500/20"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-2xl" aria-hidden="true">
                    {feature.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg font-bold text-white mb-2">{feature.title}</h4>
                    <p className="text-purple-200 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-gradient-to-br from-blue-900/40 to-purple-900/40 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-blue-500/30 mb-12"
        >
          <h3 className="text-2xl font-bold text-white mb-6 text-center">What a circle is, and isn&apos;t</h3>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {facts.map((fact) => (
              <li key={fact} className="flex items-start gap-3 bg-white/5 rounded-lg p-4">
                <span className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm mt-0.5" aria-hidden="true">
                  ✓
                </span>
                <span className="text-purple-100 text-sm leading-relaxed">{fact}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-purple-800/50 to-pink-800/50 backdrop-blur-sm rounded-2xl p-8 sm:p-10 border border-purple-500/40">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Start a circle
            </h3>
            <p className="text-purple-200 text-lg mb-8 max-w-2xl mx-auto">
              It&apos;s free, and it takes a minute.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                type="button"
                onClick={onJoinClick}
                className="group px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold rounded-xl transition-colors shadow-lg flex items-center gap-2"
              >
                <Users aria-hidden="true" className="w-5 h-5" />
                Start or join a circle
                <ArrowRight aria-hidden="true" className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-colors border border-purple-400/30 hover:border-purple-400/60"
              >
                Learn more about the app
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FriendCirclesPromo;
